import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SbButton from '@Components/Button';

type Props = {
  open: boolean;
  data: any;
  onClose: () => void;
  onCreateInvoice: () => void;
};

export default function DiscrepancyModal({ open, data, onClose, onCreateInvoice }: Props) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!data?.reportUrl) return;

    setDownloading(true);
    try {
      window.open(data.reportUrl, '_blank');
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setDownloading(false);
    }
  };

  // New logic: has discrepancies if reportUrl exists and is not null
  const hasDiscrepancies = data?.reportUrl !== null && data?.reportUrl !== undefined;

  if (!data) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasDiscrepancies ? (
          <>
            <WarningIcon color="warning" />
            Shipment Charges Discrepancy Found
          </>
        ) : (
          <>
            <CheckCircleIcon color="success" />
            No Discrepancies Found
          </>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {hasDiscrepancies
            ? 'We found some discrepancies between your uploaded data and our records. Please review the report below.'
            : 'All shipment charges match our records. You can proceed with invoice creation.'}
        </Typography>

        {hasDiscrepancies && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Discrepancies found
            </Typography>
            <Typography variant="body2">
              Some entries in your uploaded sheet contain values that differ from our shipment data. You can either
              review the mismatches or continue with invoice creation.
            </Typography>
          </Alert>
        )}

        {!hasDiscrepancies && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              All charges match perfectly. You can safely proceed with invoice creation.
            </Typography>
          </Alert>
        )}

        {/* Download Report Section - Only show if reportUrl exists */}
        {hasDiscrepancies && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <SbButton
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
              sx={{ mb: 1 }}
            >
              {downloading ? 'Downloading...' : 'Download Detailed Report'}
            </SbButton>
            <Typography variant="caption" display="block" color="textSecondary">
              Download the complete discrepancy report for detailed analysis
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SbButton onClick={onClose}>{hasDiscrepancies ? 'Review Manually' : 'Close'}</SbButton>
        <SbButton onClick={onCreateInvoice} variant="contained" color={hasDiscrepancies ? 'warning' : 'primary'}>
          {hasDiscrepancies ? 'Create Invoice Anyway' : 'Create Invoice'}
        </SbButton>
      </DialogActions>
    </Dialog>
  );
}
